import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAttendancesIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensAttendancesIndicatorC3";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC3CitizensAttendancesFilters } from "./IndicatorEsfC3CitizensAttendancesFilters";
import { IndicatorEsfC3CitizensAttendancesTable } from "./IndicatorEsfC3CitizensAttendancesTable";
import { IndicatorEsfC3CitizenAttendancesHistory } from "./IndicatorEsfC3CitizenAttendancesHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3CitizensAttendancesProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedAttendances: Paginated<DetailedCitizenAttendancesIndicatorC3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedIndicatorC3: () => Promise<DetailedCitizenAttendancesIndicatorC3Data[]>;
};

export function IndicatorEsfC3CitizensAttendances({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedAttendances,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedIndicatorC3: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC3CitizensAttendancesProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAttendances, setCitizenWithAttendances] =
		useState<DetailedCitizenAttendancesIndicatorC3Data>(
			new DetailedCitizenAttendancesIndicatorC3Data()
		);
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenWithAttendances: DetailedCitizenAttendancesIndicatorC3Data
	) {
		setIsLoadingCitizenWithAttendances(true);
		setCitizenWithAttendances(citizenWithAttendances);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC1AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Atendimentos - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC1AttendancesToGenericObjectArray(
		citizensWithAttendances: DetailedCitizenAttendancesIndicatorC3Data[]
	) {
		return citizensWithAttendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: getAge(citizenWithAttendances.birthdate),
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Equipe: citizenWithAttendances.healthTeam?.name ?? "",
					Ine: citizenWithAttendances.healthTeam?.ine ?? "",
					Microarea: citizenWithAttendances.area ?? "FA",
					Dum: dayjs(citizenWithAttendances.dumDate).format("DD/MM/YYYY"),
					Dpp: dayjs(citizenWithAttendances.dppDate).format("DD/MM/YYYY"),
					IG: citizenWithAttendances.gestationalAge ?? "",
					"1ª Consulta": citizenWithAttendances.firstAttendance?.date
						? dayjs(citizenWithAttendances.firstAttendance.date).format("DD/MM/YYYY")
						: "",
					"Status 1º Consulta": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.firstAttendanceStatus
					),
					Consultas: citizenWithAttendances.pregnancyAttendances?.length ?? 0,
					"Status 7 Consultas": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.sevenPregnancyAttendancesStatus
					),
					"Registros Peso e Altura":
						citizenWithAttendances.weightAndHeightMetricsRegisters?.length ?? 0,
					"Status 7 Registros Peso e Altura": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.sevenWeightAndHeightMetricsRegistersStatus
					),
					"Aferições de PA":
						citizenWithAttendances.bloodPressureMeasurements?.length ?? 0,
					"Status 7 Aferições de PA": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.sevenBloodPressureMeasurementsStatus
					),
					"Consulta Puerperal": citizenWithAttendances.puerperalAttendance?.date
						? dayjs(citizenWithAttendances.puerperalAttendance.date).format("DD/MM/YYYY")
						: "",
					"Status Consulta Puerperal": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.puerperalAttendanceStatus
					),
					"Consultas Odontológicas": citizenWithAttendances.dentalAttendances?.length ?? 0,
					"Status Consulta Odontológica": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithAttendances.dentalAttendancesStatus
					)
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3CitizensAttendancesFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3CitizensAttendancesTable
							citizensWithAttendances={paginatedAttendances.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedAttendances?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedAttendances.pageSize}
								totalPages={paginatedAttendances.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC3CitizenAttendancesHistory
							citizenWithAttendances={citizenWithAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
